import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { getRandomColor } from 'pages/admin/CostDashboard/helper';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { commify } from 'shared/utils/strings';

interface PieChartWidgetProps<T extends Record<string, any>> {
  data: T[];
  nameKey: keyof T;
  dataKey: keyof T;
  valueFormatter?: (value: number) => string;
  title: string;
  height?: number;
  isLoading?: boolean;
  showTotal?: boolean;
  description?: string;
}

const CostPieChart = <T extends Record<string, any>>({
  data,
  dataKey,
  description,
  height = 300,
  isLoading = false,
  nameKey,
  showTotal = false,
  title,
  valueFormatter = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
}: PieChartWidgetProps<T>) => {
  const total = data.reduce((sum, entry) => sum + Number(entry[dataKey]), 0);

  const chartData = data.map(item => ({
    name: String(item[nameKey]),
    value: Number(item[dataKey]),
    fill: getRandomColor(),
    percent: ((Number(item[dataKey]) / total) * 100).toFixed(1),
  }));

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: 1,
        p: 3,
        width: '100%',
      }}
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography mb={2} variant="h6">
            {title}
          </Typography>
          {showTotal && <Typography>{`Total: $${commify(total.toFixed(2))}`}</Typography>}
        </Stack>

        {!!description && (
          <Typography
            sx={{ color: theme => theme.colors.functional.text.lint }}
            variant="subtitle1"
          >
            {description}
          </Typography>
        )}
      </Stack>

      <ResponsiveContainer height={height} width="100%">
        {isLoading ? (
          <Skeleton height={height} variant="rectangular" />
        ) : data.length === 0 ? (
          <Stack alignItems="center" justifyContent="center">
            <Typography variant="caption">No Data available</Typography>
          </Stack>
        ) : (
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              isAnimationActive
              labelLine={false}
              nameKey="name"
              outerRadius={100}
            >
              {chartData.map((entry, index) => (
                <Cell fill={entry.fill} key={`cell-${index}`} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => valueFormatter(value)} />
            <Legend
              align="center"
              content={({ payload }) => (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="center"
                  spacing={3}
                  sx={{ mt: 2 }}
                >
                  {payload?.map((entry, idx) => {
                    const item = chartData.find(d => d.name === entry.value);

                    return (
                      <Stack alignItems="center" direction="row" key={idx} spacing={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: entry.color,
                          }}
                        />
                        <Typography fontSize="7px" fontWeight={600} variant="caption">
                          {entry.value.toUpperCase()}:
                        </Typography>
                        <Typography fontSize="8px" variant="caption">
                          {valueFormatter(item?.value ?? 0)} ({item?.percent}%)
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              iconSize={8}
              iconType="rect"
              verticalAlign="bottom"
            />
          </PieChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

export default CostPieChart;
